<template>
  <div class="content-with-spacing progress-migration-planner" :class="sidebarClass">
    <div class="heading-container">
      <h1>Progress Migration</h1>
    </div>

    <div class="panel panel-body">
      <p class="info-text">Select a learner, source programme, and destination programme to generate a personalised migration plan.</p>

      <k-form
        class="progress-migration-form"
        :columns="1"
        @submit.prevent="generatePlan"
      >
        <k-form-field
          label="Learner"
          field-id="progress-migration-learner"
          :required="true"
        >
          <select-user
            v-model="learner"
            search-text="Search for learner"
            @reset-meeting-owner="clearLearner"
          />
        </k-form-field>

        <k-form-field
          label="Source programme"
          field-id="progress-migration-source-programme"
          :required="true"
        >
          <div class="programme-field-row">
            <div class="programme-field-dropdown">
              <k-dropdown
                id="progress-migration-source-programme"
                v-model="sourceProgrammeId"
                :options="sourceProgrammeOptions"
                :disabled="!learnerId || learnerProgrammesLoading"
                :placeholder="sourceProgrammePlaceholder"
              />
            </div>
            <k-status-pill
              v-if="sourceProgrammeId != null"
              class="programme-group-pill"
              type="neutral"
              :status-value="placeholderProgrammeGroupLabel"
            />
          </div>
        </k-form-field>

        <k-form-field
          label="Destination programme"
          field-id="progress-migration-destination-programme"
          :required="true"
        >
          <div class="programme-field-row">
            <div class="programme-field-dropdown">
              <k-dropdown
                id="progress-migration-destination-programme"
                v-model="destinationProgrammeId"
                :options="destinationProgrammeOptions"
                :disabled="!learnerId || learnerProgrammesLoading"
                :placeholder="destinationProgrammePlaceholder"
              />
            </div>
            <k-status-pill
              v-if="destinationProgrammeId != null"
              class="programme-group-pill"
              type="neutral"
              :status-value="placeholderProgrammeGroupLabel"
            />
          </div>
          <span v-if="programmesMatch" class="helper-text" role="alert">
            Source and destination programme must be different.
          </span>
        </k-form-field>

        <k-form-field>
          <button
            type="submit"
            class="btn btn-primary"
            :disabled="!generatePlanEnabled"
          >
            Generate plan
          </button>
        </k-form-field>
      </k-form>
    </div>
  </div>
</template>

<style scoped>
.progress-migration-form {
  margin-top: 1rem;
  max-width: 36rem;
}

.progress-migration-form :deep(.user-selection-actions) {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.5rem;
  width: 100%;
}

.progress-migration-form :deep(.user-selection-actions .search-button) {
  flex: 1;
  min-width: 0;
}

.progress-migration-form :deep(.user-profile-card) {
  margin-top: 0.75rem;
  margin-bottom: 0;
}

.programme-field-row {
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
  flex-wrap: wrap;
}

.programme-field-dropdown {
  flex: 1;
  min-width: 0;
}

.programme-group-pill {
  flex-shrink: 0;
}

.helper-text {
  display: block;
  margin-top: 1rem;
  font-size: 0.9em;
  color: var(--kate-type-warning) !important;
  font-style: italic;
}

</style>

<script>
import KDropdown from '@base-components/k-dropdown.vue';
import KStatusPill from '@base-components/k-status-pill.vue';
import { KForm, KFormField } from '@base-components/forms';
import SidebarMixin from '@mixins/sidebar-mixin';
import SelectUser from '../meetings/components/select-user.vue';
import {
  PLACEHOLDER_PROGRAMME_GROUP_LABEL,
  SELECT_LEARNER_FIRST_PLACEHOLDER,
} from './fixtures/progress-migration-start';

export default {
  name: 'ProgressMigrationPlanner',

  components: {
    KDropdown,
    KForm,
    KFormField,
    KStatusPill,
    SelectUser,
  },

  mixins: [SidebarMixin],

  data() {
    return {
      learner: {},
      sourceProgrammeId: undefined,
      destinationProgrammeId: undefined,
      placeholderProgrammeGroupLabel: PLACEHOLDER_PROGRAMME_GROUP_LABEL,
      learnerProgrammes: [],
      learnerProgrammesLoading: false,
    };
  },

  computed: {
    learnerId() {
      return this.learner?.id;
    },

    sourceProgrammeOptions() {
      return this.learnerId ? this.learnerProgrammes : [];
    },

    destinationProgrammeOptions() {
      return this.learnerId ? this.learnerProgrammes : [];
    },

    sourceProgrammePlaceholder() {
      return this.programmeDropdownPlaceholder('Select source programme');
    },

    destinationProgrammePlaceholder() {
      return this.programmeDropdownPlaceholder('Select destination programme');
    },

    programmesMatch() {
      return this.sourceProgrammeId !== undefined
        && this.sourceProgrammeId === this.destinationProgrammeId;
    },

    generatePlanEnabled() {
      const required = [
        this.learnerId,
        this.sourceProgrammeId,
        this.destinationProgrammeId,
      ];
      return required.every(value => value != null) && !this.programmesMatch;
    },
  },

  watch: {
    learnerId() {
      this.sourceProgrammeId = undefined;
      this.destinationProgrammeId = undefined;
      this.learnerProgrammes = [];

      if (this.learnerId != null) {
        this.fetchLearnerProgrammes(this.learnerId);
      }
    },
  },

  beforeMount() {
    this.$Loading.finish();
  },

  methods: {
    fetchLearnerProgrammes(studentId) {
      this.learnerProgrammesLoading = true;
      this.$logger.info('Getting student programmes', { studentId }, true);
      return this.$http.get(`/api/profile/user/${studentId}/programmes?dashboard=true`)
        .then(result => {
          this.learnerProgrammes = result.data.programmes ?? [];
        })
        .catch(error => {
          this.$logger.error('Error getting student programmes', { studentId }, error);
          this.$ktoast.showError(error);
          this.learnerProgrammes = [];
        })
        .then(() => {
          this.learnerProgrammesLoading = false;
        });
    },

    clearLearner() {
      this.learner = {};
    },

    programmeDropdownPlaceholder(selectProgrammeLabel) {
      if (this.learnerId == null) {
        return SELECT_LEARNER_FIRST_PLACEHOLDER;
      }
      if (this.learnerProgrammesLoading) {
        return 'Loading programmes…';
      }
      if (this.learnerProgrammes.length === 0) {
        return 'No programmes found';
      }
      return selectProgrammeLabel;
    },

    generatePlan() {
      if (!this.generatePlanEnabled) {
        return;
      }
      this.$logger.info('Not implemented');
    },
  },
};
</script>
